// The "Check COI" multi-step form (COI-9, SoftR conditional-form parity — 07a §1).
// Step 1 "Reviewer" collects identity fields with the LIVE conditional "Justification if not
// Institutional Email" textarea (COI-14, via isInstitutionalEmail). Step 2 "Flags" collects the
// backup-reviewer variant (COI-13: "Invite backup for…" + justification) and the Affiliation COI
// checkbox + justification. Submit POSTs /api/coi/proposals — non-blocking (COI-1): a 201 means
// a PENDING proposal was created ("Proposed Reviewer Submitted!", form resets — SoftR refresh
// parity); the engine resolves it async and the row updates live over SSE. Duplicates fast-reject
// with 409 already_proposed (COI-15).
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isInstitutionalEmail, type CheckCoiProposalBody, type ProposalView } from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { Badge, Button, Field, Input, Select, Textarea } from "../../components/ui.js";

interface CheckCoiResponse {
  id: string;
  status: string;
  jobId: string;
  proposal?: ProposalView;
}

const EMPTY = {
  firstName: "",
  lastName: "",
  reviewerEmail: "",
  emailJustification: "",
  affiliation: "",
  backupReviewer: false,
  backupForId: "",
  backupJustification: "",
  affiliationCoi: false,
  affiliationCoiJustification: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function CheckCoiForm({
  preprintId,
  proposals,
  source,
}: {
  preprintId: string;
  /** Existing proposals on this preprint — the "Invite backup for…" options (COI-13). */
  proposals: ProposalView[];
  /** "Student" from the on-page form; "Manual" from "Manually Add Suggested Reviewer". */
  source: "Student" | "Manual";
}): JSX.Element {
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2>(1);
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const set = <K extends keyof typeof EMPTY>(key: K, value: (typeof EMPTY)[K]): void => {
    setF((prev) => ({ ...prev, [key]: value }));
    setError(null);
    setSuccess(false);
  };

  // COI-14: live conditional step — the justification field appears as soon as the email
  // stops looking institutional (free-mail domains), exactly like the SoftR conditional form.
  const needsEmailJustification = f.reviewerEmail.trim() !== "" && !isInstitutionalEmail(f.reviewerEmail);

  const submit = useMutation({
    mutationFn: () => {
      const body: CheckCoiProposalBody = {
        preprintId,
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        reviewerEmail: f.reviewerEmail.trim(),
        // Gated on the LIVE condition (not just non-empty state): if the user corrected the
        // email to an institutional address, a stale justification must not ride along.
        ...(needsEmailJustification && f.emailJustification.trim()
          ? { emailJustification: f.emailJustification.trim() }
          : {}),
        affiliation: f.affiliation.trim(),
        backupReviewer: f.backupReviewer,
        ...(f.backupReviewer && f.backupForId ? { backupForId: f.backupForId } : {}),
        ...(f.backupReviewer && f.backupJustification.trim() ? { backupJustification: f.backupJustification.trim() } : {}),
        affiliationCoi: f.affiliationCoi,
        ...(f.affiliationCoi && f.affiliationCoiJustification.trim()
          ? { affiliationCoiJustification: f.affiliationCoiJustification.trim() }
          : {}),
        proposalSource: source,
      };
      return api.post<CheckCoiResponse>("/coi/proposals", body);
    },
    onSuccess: () => {
      // SoftR parity: success message + refresh form. The new row appears in the Student
      // Proposed list via invalidation and resolves live via SSE (coi.completed → "proposals").
      void qc.invalidateQueries({ queryKey: ["proposals"] });
      setF(EMPTY);
      setStep(1);
      setError(null);
      setSuccess(true);
    },
    onError: (err) => {
      setSuccess(false);
      if (err instanceof ApiError && err.status === 409) {
        setError("This reviewer was already proposed for this preprint."); // COI-15
        return;
      }
      if (err instanceof ApiError && err.status === 400 && err.body && typeof err.body === "object") {
        const issues = (err.body as { issues?: Array<{ message?: string }> }).issues;
        const first = issues?.[0]?.message;
        setError(first ?? "Please check the form and try again.");
        return;
      }
      setError("Could not submit this reviewer. Please try again.");
    },
  });

  // Client-side validation mirrors the API's CheckCoiProposalSchema (which re-enforces it).
  const validateStep1 = (): string | null => {
    if (!f.firstName.trim() || !f.lastName.trim()) return "First and last name are required.";
    if (!EMAIL_RE.test(f.reviewerEmail.trim())) return "A valid reviewer email is required.";
    if (needsEmailJustification && !f.emailJustification.trim())
      return "Justification required for a non-institutional email (COI-14).";
    if (!f.affiliation.trim()) return "Affiliation is required.";
    return null;
  };
  const validateStep2 = (): string | null => {
    if (f.backupReviewer && !f.backupForId) return "Select which reviewer this is a backup for (COI-13).";
    if (f.backupReviewer && !f.backupJustification.trim()) return "Backup justification required (COI-13).";
    if (f.affiliationCoi && !f.affiliationCoiJustification.trim())
      return "Justification for the affiliation conflict of interest is required.";
    return null;
  };

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const problem = validateStep1() ?? validateStep2();
        if (problem) {
          setError(problem);
          return;
        }
        submit.mutate();
      }}
    >
      <div className="flex items-center gap-2">
        <Badge tone={step === 1 ? "blue" : "slate"}>Step 1 — Reviewer</Badge>
        <span className="text-slate-300">→</span>
        <Badge tone={step === 2 ? "blue" : "slate"}>Step 2 — Flags</Badge>
        {source === "Manual" && <Badge tone="violet">Manual</Badge>}
      </div>

      {success && (
        <div className="rounded-md border border-green-200 bg-green-50 p-2 text-sm font-medium text-green-800" role="status">
          Proposed Reviewer Submitted!
        </div>
      )}

      {step === 1 ? (
        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Reviewer First Name">
              <Input value={f.firstName} onChange={(e) => set("firstName", e.target.value)} required />
            </Field>
            <Field label="Reviewer Last Name">
              <Input value={f.lastName} onChange={(e) => set("lastName", e.target.value)} required />
            </Field>
          </div>
          <Field label="Reviewer Email">
            <Input type="email" value={f.reviewerEmail} onChange={(e) => set("reviewerEmail", e.target.value)} required />
          </Field>
          {needsEmailJustification && (
            <Field label="Justification if not Institutional Email (required)">
              <Textarea
                value={f.emailJustification}
                onChange={(e) => set("emailJustification", e.target.value)}
                placeholder="Why is a non-institutional email acceptable for this reviewer?"
              />
            </Field>
          )}
          <Field label="Affiliation">
            <Input value={f.affiliation} onChange={(e) => set("affiliation", e.target.value)} required />
          </Field>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                const problem = validateStep1();
                if (problem) {
                  setError(problem);
                  return;
                }
                setError(null);
                setStep(2);
              }}
            >
              Next
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="flex items-center gap-1.5 text-sm text-slate-700">
            <input type="checkbox" checked={f.backupReviewer} onChange={(e) => set("backupReviewer", e.target.checked)} />
            Back up reviewer?
          </label>
          {f.backupReviewer && (
            <div className="space-y-2 rounded-md border border-slate-100 p-2">
              <Field label="Invite backup for…">
                <Select value={f.backupForId} onChange={(e) => set("backupForId", e.target.value)} className="w-full">
                  <option value="">Select a reviewer…</option>
                  {proposals.map((p) => (
                    <option key={p.id} value={p.id}>
                      {[p.firstName, p.lastName].filter(Boolean).join(" ") || p.reviewerEmail || p.id}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Backup justification (required)">
                <Textarea value={f.backupJustification} onChange={(e) => set("backupJustification", e.target.value)} />
              </Field>
            </div>
          )}
          <label className="flex items-center gap-1.5 text-sm text-slate-700">
            <input type="checkbox" checked={f.affiliationCoi} onChange={(e) => set("affiliationCoi", e.target.checked)} />
            Affiliation COI
          </label>
          {f.affiliationCoi && (
            <Field label="Justification for Affiliation Conflict of Interest (required)">
              <Textarea
                value={f.affiliationCoiJustification}
                onChange={(e) => set("affiliationCoiJustification", e.target.value)}
              />
            </Field>
          )}
          <div className="flex items-center justify-between pt-1">
            <Button type="button" variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button type="submit" disabled={submit.isPending}>
              {submit.isPending ? "Submitting…" : "Check COI"}
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" role="alert">
          {error}
        </div>
      )}
    </form>
  );
}
